import React from 'react';
import { useCentrosList } from './hooks/useCentrosList';

export type { Centro } from './types/centro.types';

interface CentrosListProps {
    proveedorId?: string | number;
}

const CentrosList: React.FC<CentrosListProps> = ({ proveedorId }) => {
    const {
        centros,
        isLoading,
        error,
        searchTerm,
        setSearchTerm,
        page,
        setPage,
        limit,
        setLimit,
        totalRecords,
        sortBy,
        sortOrder,
        totalPages,
        fetchCentros,
        handleSort,
        getPageNumbers,
    } = useCentrosList(proveedorId);

    const renderSortIcon = (column: string) => {
        if (sortBy !== column) {
            return <i className="fas fa-sort text-muted ms-1 opacity-25" />;
        }
        return sortOrder === 'asc' ? (
            <i className="fas fa-sort-up text-primary ms-1" />
        ) : (
            <i className="fas fa-sort-down text-primary ms-1" />
        );
    };

    const baseUrlAdd = proveedorId ? `/centro?accion=add&idProveedor=${proveedorId}` : '/centro?accion=add';
    const colSpan = proveedorId ? 4 : 5;

    return (
        <div className="card shadow-sm border-0 animate__animated animate__fadeIn">
            <div className="card-header border-bottom py-3 d-flex justify-content-between align-items-center">
                <h5 className="mb-0 fw-bold">
                    <i className="fas fa-building text-primary me-2" />
                    {proveedorId ? 'Centros de Servicio Asignados' : 'Directorio de Centros de Servicio'}
                </h5>
                {proveedorId && (
                    <a href={baseUrlAdd} className="btn btn-primary btn-sm px-4 fw-bold shadow-sm">
                        <i className="fas fa-plus me-1" /> Nuevo Centro
                    </a>
                )}
            </div>

            <div className="card-body p-4">
                <div className="row g-3 mb-4">
                    <div className="col-md-12 col-lg-6">
                        <div className="input-group shadow-sm">
                            <span className="input-group-text border-end-0 text-primary">
                                <i className="fas fa-search" />
                            </span>
                            <input
                                type="text"
                                className="form-control border-start-0 ps-0"
                                placeholder="Buscar por sucursal, asesor o email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="col-lg-6 text-end">
                        <button
                            type="button"
                            className="btn border text-muted"
                            onClick={fetchCentros}
                            title="Actualizar datos"
                            disabled={isLoading}
                        >
                            <i className={`fas fa-sync-alt ${isLoading ? 'fa-spin' : ''}`} /> Refrescar
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="alert alert-danger border-0 shadow-sm">
                        <i className="fas fa-exclamation-triangle me-2" /> {error}
                    </div>
                )}

                <div className="table-responsive rounded border shadow-sm mb-3">
                    <table className="table table-hover align-middle mb-0">
                        <thead className="text-secondary">
                            <tr>
                                {!proveedorId && (
                                    <th
                                        className="ps-4 user-select-none cursor-pointer"
                                        onClick={() => handleSort('proveedor')}
                                    >
                                        Proveedor {renderSortIcon('proveedor')}
                                    </th>
                                )}
                                <th
                                    className={`user-select-none cursor-pointer ${proveedorId ? 'ps-4' : ''}`}
                                    onClick={() => handleSort('sucursal')}
                                >
                                    Sucursal {renderSortIcon('sucursal')}
                                </th>
                                <th className="text-center">Ubicación</th>
                                <th
                                    className="text-center user-select-none cursor-pointer"
                                    onClick={() => handleSort('asesor')}
                                >
                                    Contacto {renderSortIcon('asesor')}
                                </th>
                                <th className="text-end pe-4">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan={colSpan} className="text-center py-5 text-muted">
                                        <div className="spinner-border text-primary mb-2" role="status" />
                                        <p className="mb-0 small">Consultando base de datos...</p>
                                    </td>
                                </tr>
                            ) : centros.length === 0 ? (
                                <tr>
                                    <td colSpan={colSpan} className="text-center py-5 text-muted fst-italic">
                                        <i className="fas fa-building fa-2x mb-3" />
                                        <br />
                                        No se encontraron centros con los filtros actuales.
                                    </td>
                                </tr>
                            ) : (
                                centros.map((centro) => (
                                    <tr key={centro.clave_centro}>
                                        {!proveedorId && <td className="ps-4 fw-bold">{centro.proveedor_nombre}</td>}
                                        <td className={`fw-bold text-primary ${proveedorId ? 'ps-4' : ''}`}>
                                            {centro.sucursal}
                                        </td>
                                        <td className="text-center small">
                                            {centro.estado}{' '}
                                            {centro.ciudad && <span className="text-muted d-block">{centro.ciudad}</span>}
                                        </td>
                                        <td className="text-center small">
                                            <span className="d-block fw-bold">{centro.asesor}</span>
                                            <span className="text-muted d-block">
                                                <i className="fas fa-phone-alt me-1" style={{ fontSize: '0.7rem' }} />
                                                {centro.telefono}
                                            </span>
                                        </td>
                                        <td className="text-end pe-4">
                                            <a
                                                href={`/centro?accion=view&id=${centro.id_encriptado}&idProveedor=${btoa(centro.clave_persona_proveedor.toString())}`}
                                                className="btn btn-sm border text-primary me-2 rounded-circle"
                                                title="Ver detalle"
                                            >
                                                <i className="far fa-eye" />
                                            </a>
                                            <a
                                                href={`/centro?accion=edit&id=${centro.id_encriptado}&idProveedor=${btoa(centro.clave_persona_proveedor.toString())}`}
                                                className="btn btn-sm border text-warning rounded-circle"
                                                title="Editar"
                                            >
                                                <i className="far fa-pencil-alt" />
                                            </a>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-3">
                    <div className="d-flex align-items-center mb-3 mb-md-0">
                        <span className="text-muted small me-2">Mostrar:</span>
                        <select
                            className="form-select form-select-sm shadow-sm w-auto"
                            value={limit}
                            onChange={(e) => {
                                setLimit(Number(e.target.value));
                                setPage(1);
                            }}
                            disabled={isLoading}
                        >
                            <option value="10">10 registros</option>
                            <option value="25">25 registros</option>
                            <option value="50">50 registros</option>
                            <option value="100">100 registros</option>
                        </select>
                        <span className="text-muted small ms-3 d-none d-sm-inline">
                            Total: <strong>{totalRecords}</strong> resultados.
                        </span>
                    </div>

                    {totalPages > 1 && (
                        <ul className="pagination pagination-sm mb-0 shadow-sm">
                            <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                                <button
                                    type="button"
                                    className="page-link"
                                    onClick={() => setPage(1)}
                                    disabled={page === 1 || isLoading}
                                    title="Primera página"
                                >
                                    <i className="fas fa-angle-double-left" />
                                </button>
                            </li>
                            <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                                <button
                                    type="button"
                                    className="page-link"
                                    onClick={() => setPage(page - 1)}
                                    disabled={page === 1 || isLoading}
                                    title="Página anterior"
                                >
                                    <i className="fas fa-angle-left" />
                                </button>
                            </li>
                            {getPageNumbers().map((pageNum) => (
                                <li key={pageNum} className={`page-item ${page === pageNum ? 'active' : ''}`}>
                                    <button
                                        type="button"
                                        className="page-link"
                                        onClick={() => setPage(pageNum)}
                                        disabled={isLoading}
                                    >
                                        {pageNum}
                                    </button>
                                </li>
                            ))}
                            <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                                <button
                                    type="button"
                                    className="page-link"
                                    onClick={() => setPage(page + 1)}
                                    disabled={page === totalPages || isLoading}
                                    title="Página siguiente"
                                >
                                    <i className="fas fa-angle-right" />
                                </button>
                            </li>
                            <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                                <button
                                    type="button"
                                    className="page-link"
                                    onClick={() => setPage(totalPages)}
                                    disabled={page === totalPages || isLoading}
                                    title="Última página"
                                >
                                    <i className="fas fa-angle-double-right" />
                                </button>
                            </li>
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CentrosList;
